<template>
  <!-- Opening balance row for one account -->
  <tr class="budget-opening-row border-b border-border/50">
    <td class="sticky left-0 z-20 bg-card budget-sticky-cell px-3 py-2 text-xs font-black uppercase tracking-wider text-muted-foreground whitespace-nowrap w-52 min-w-52">
      <div class="flex items-center gap-2">
        <span class="text-primary/70">⬛</span>
        Solde début de mois
      </div>
    </td>

    <td
      v-for="month in 12"
      :key="month"
      class="min-w-[90px] p-0"
      :class="[
        month === currentMonth ? 'bg-primary/5' : '',
        month % 2 === 0 ? 'bg-muted/30' : ''
      ]"
    >
      <div class="group relative w-full h-full">
    <!-- View mode (always rendered to keep column width stable, hidden when editing) -->
    <button
      class="w-full flex items-center justify-end gap-1 px-2 py-2 text-right text-xs font-bold tabular-nums transition-colors hover:bg-muted/40 rounded focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary"
      :class="[balanceClass(month), { 'invisible': editingMonth === month }]"
            @click="!isReadOnlyMode && startEdit(month)"
            :title="balanceData[month]?.isManual ? 'Valeur forcée manuellement — cliquer pour modifier' : 'Cliquer pour forcer une valeur de départ'"
            :aria-label="`Solde début ${monthNames[month - 1]} : ${displayBalance(month)}`"
          >
            <span v-if="balanceData[month]?.isManual" class="text-[9px] opacity-40 select-none" title="Valeur manuelle">🔒</span>
            {{ displayBalance(month) }}
          </button>

    <button
      v-if="!isReadOnlyMode && balanceData[month]?.isManual && editingMonth !== month"
      class="absolute left-0.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-zinc-600 text-white text-[9px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500 z-10"
      @click.stop="emit('reset', month)"
      title="Réinitialiser (recalcul automatique)"
      aria-label="Réinitialiser le solde de début de mois"
    >×</button>

    <!-- Edit mode -->
    <input
      v-if="editingMonth === month"
          ref="inputRef"
          v-model="editValue"
          type="number"
      step="1"
      class="absolute inset-0 w-full min-w-0 px-2 py-2 text-right text-xs font-bold bg-primary/10 border border-primary rounded outline-none tabular-nums text-foreground [-moz-appearance:_textfield] [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none z-20"
      @keydown.enter="confirmEdit(month)"
          @keydown.escape="cancelEdit"
          @blur="confirmEdit(month)"
          :aria-label="`Saisie solde début ${monthNames[month - 1]}`"
        />
      </div>
    </td>

    <!-- Empty totals column -->
    <td class="w-28 min-w-[7rem]"></td>
  </tr>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { useBudget } from '../../../composables/useBudget'

// ── Props ─────────────────────────────────────────────────────────────────────

const props = defineProps<{
  /** resolvedBalances[month] = { amount, isManual } */
  balanceData:   Record<number, { amount: number | null; isManual: boolean }>
  currentMonth:  number
}>()

// ── Emits ─────────────────────────────────────────────────────────────────────

const emit = defineEmits<{
  save:  [month: number, amount: number]
  reset: [month: number]
}>()

// ── Constants ─────────────────────────────────────────────────────────────────

const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

// ── State ─────────────────────────────────────────────────────────────────────

const { isReadOnlyMode } = useBudget()
const editingMonth = ref<number | null>(null)
const editValue    = ref('')
const inputRef     = ref<HTMLInputElement | null>(null)

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount: number | null): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function displayBalance(month: number): string {
  return formatCurrency(props.balanceData[month]?.amount ?? null)
}

function balanceClass(month: number): string {
  const amount = props.balanceData[month]?.amount
  if (amount === null || amount === undefined) return 'text-muted-foreground/30'
  if (amount >= 0) return 'text-sky-500 dark:text-sky-400'
  return 'text-orange-500 dark:text-orange-400'
}

// ── Actions ───────────────────────────────────────────────────────────────────

function startEdit(month: number): void {
  if (isReadOnlyMode.value) return
  editValue.value    = String(props.balanceData[month]?.amount ?? '')
  editingMonth.value = month
  nextTick(() => inputRef.value?.select())
}

function confirmEdit(month: number): void {
  if (editingMonth.value !== month) return
  editingMonth.value = null
  const stringValue = String(editValue.value ?? '')
  const trimmed = stringValue.trim()
  if (trimmed === '') {
    if (props.balanceData[month]?.isManual) {
      emit('reset', month)
    }
    return
  }

  const parsed = parseFloat(trimmed)
  if (!isNaN(parsed)) {
    emit('save', month, parsed)
  }
}

function cancelEdit(): void {
  editingMonth.value = null
}
</script>
